package com.tovtalk.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.comments.CommentsViewModel
import com.tovtalk.ui.theme.TovTalkTheme

/**
 * Side panel version of comments for Expanded window size class.
 * Simplified version of CommentsBottomSheet, displayed as a persistent panel.
 *
 * NOTE: CommentsViewModel is registered as a factory in Koin (not singleton),
 * so each CommentsPanel call site gets its own isolated ViewModel instance.
 * currentPostId in the ViewModel is set via loadComments(postId) in LaunchedEffect below.
 */
@Composable
fun CommentsPanel(
    postId: Int,
    onCommentAdded: () -> Unit = {},
    modifier: Modifier = Modifier,
    viewModel: CommentsViewModel = disposableKoinInject()
) {
    var composerText by remember { mutableStateOf("") }
    val commentsState by viewModel.commentsState.collectAsState()
    val addCommentState by viewModel.addCommentState.collectAsState()
    val listState = rememberLazyListState()

    LaunchedEffect(postId) {
        viewModel.loadComments(postId, refresh = true)
    }

    LaunchedEffect(addCommentState) {
        if (addCommentState is UiState.Success<*>) {
            composerText = ""
            onCommentAdded()
            viewModel.resetAddCommentState()
        }
    }

    // Load more when nearing the end
    val shouldLoadMore by remember {
        derivedStateOf {
            val lastVisibleIndex = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
            lastVisibleIndex >= commentsState.items.size - 3 && commentsState.hasMore && !commentsState.isLoadingMore
        }
    }
    LaunchedEffect(shouldLoadMore) {
        if (shouldLoadMore) viewModel.loadMore()
    }

    Column(
        modifier = modifier
            .width(300.dp)
            .fillMaxHeight()
            .background(TovTalkTheme.colors.surfaceElevated)
    ) {
        // Header
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            Text(
                text = "תגובות",
                style = TovTalkTheme.typography.titleSmall,
                color = TovTalkTheme.colors.textPrimary
            )
        }

        HorizontalDivider(
            color = TovTalkTheme.colors.border,
            thickness = TovTalkTheme.sizes.dividerThickness
        )

        // Content
        when {
            commentsState.isLoading && commentsState.items.isEmpty() -> {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = TovTalkTheme.colors.brand)
                }
            }

            commentsState.items.isEmpty() && !commentsState.isLoading -> {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = "אין תגובות עדיין",
                        style = TovTalkTheme.typography.bodyMedium,
                        color = TovTalkTheme.colors.textSecondary
                    )
                }
            }

            else -> {
                LazyColumn(
                    state = listState,
                    modifier = Modifier.weight(1f),
                    contentPadding = PaddingValues(vertical = 8.dp, horizontal = 8.dp)
                ) {
                    items(
                        items = commentsState.items,
                        key = { it.id }
                    ) { comment ->
                        CommentPanelItem(
                            username = comment.user?.username ?: "",
                            text = comment.text ?: "",
                            avatarUrl = comment.user?.profilePhoto
                        )
                    }

                    if (commentsState.isLoadingMore) {
                        item {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(24.dp),
                                    color = TovTalkTheme.colors.brand
                                )
                            }
                        }
                    }
                }
            }
        }

        HorizontalDivider(
            color = TovTalkTheme.colors.border,
            thickness = TovTalkTheme.sizes.dividerThickness
        )

        // Input field
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 8.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            OutlinedTextField(
                value = composerText,
                onValueChange = { composerText = it },
                placeholder = {
                    Text(
                        text = "כתוב תגובה...",
                        style = TovTalkTheme.typography.bodySmall,
                        color = TovTalkTheme.colors.textTertiary
                    )
                },
                modifier = Modifier.weight(1f),
                textStyle = TovTalkTheme.typography.bodySmall,
                shape = RoundedCornerShape(20.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = TovTalkTheme.colors.brand,
                    unfocusedBorderColor = TovTalkTheme.colors.border,
                    focusedContainerColor = TovTalkTheme.colors.backgroundSecondary,
                    unfocusedContainerColor = TovTalkTheme.colors.backgroundSecondary
                ),
                singleLine = true
            )
            IconButton(
                onClick = {
                    if (composerText.isNotBlank()) {
                        viewModel.submitComment(composerText.trim())
                        composerText = ""
                    }
                },
                enabled = composerText.isNotBlank() && addCommentState !is UiState.Loading
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.Send,
                    contentDescription = "שלח",
                    tint = if (composerText.isNotBlank()) TovTalkTheme.colors.brand else TovTalkTheme.colors.textTertiary
                )
            }
        }
    }
}

@Composable
private fun CommentPanelItem(
    username: String,
    text: String,
    avatarUrl: String?,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 6.dp),
        verticalAlignment = Alignment.Top
    ) {
        TovTalkAvatar(
            imageUrl = avatarUrl,
            size = 28.dp
        )
        Spacer(modifier = Modifier.width(8.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = username,
                style = TovTalkTheme.typography.labelSmall,
                color = TovTalkTheme.colors.textSecondary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            if (text.isNotBlank()) {
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = text,
                    style = TovTalkTheme.typography.bodySmall,
                    color = TovTalkTheme.colors.textPrimary
                )
            }
        }
    }
}
